import type { PlatformContract } from "@ondit/publishing";
import { post, request } from "~/core/api";
export interface Channel {
	platform: string;
	credential_id: string | null;
	target_url: string;
	fields: Record<string, string>;
}
export interface Policy {
	enabled: boolean;
	mode: "assisted" | "review" | "automatic";
	weekly_credits: number;
	interval_days: number;
	max_pending: number;
	channels: Channel[];
	format: "text" | "cards" | "video";
	image_model: string;
	preset: string;
}
export interface PolicyRow {
	weekly_spent: number;
	held: number;
	plan_id: string;
	revision: number;
	config: Policy;
	workflow_id: string | null;
	title: string;
	objective: string;
	plan_status: string;
}
export interface Work {
	allowed: boolean;
	id: string;
	plan_id: string;
	policy_revision: number;
	config: Policy;
	run_id: string;
	item_id: string | null;
	item_title: string | null;
	current_revision: number | null;
	source_revision: number | null;
	status: string;
	reason: string | null;
	title: string;
	spent: number;
	held: number;
	created_at: string;
	outcome: string;
	evidence: string;
	variants: {
		id: string;
		revision: number;
		platform: string;
		issues: string[];
		task_id: string | null;
		state: string | null;
		result_url: string | null;
	}[];
}
export interface Overview {
	policies: PolicyRow[];
	work: Work[];
	decisions: { id: number; title: string; reason: string; created_at: string }[];
	defaults: Policy;
	platforms?: PlatformContract[];
	credentials?: { id: string; provider_id: string; name: string; account: string | null }[];
}
export const marketingKey = (brand: string, plan: string) => ["marketing", brand, plan];
const base = (brand: string) => `/agent/marketing/${brand}`;
export const overview = (brand: string, plan: string) => request<Overview>(`${base(brand)}/plans/${plan}/automation`);
export const savePolicy = (brand: string, plan: string, revision: number, config: Policy) =>
	post<{ policy: PolicyRow }>(`${base(brand)}/plans/${plan}/automation`, { expected_revision: revision, config });
export const pausePolicy = (brand: string, plan: string, revision: number) =>
	post(`${base(brand)}/plans/${plan}/automation`, { expected_revision: revision, action: "pause" });
export const runPlan = (brand: string, plan: string, work_id?: string) =>
	post(`${base(brand)}/plans/${plan}/run`, { request_id: crypto.randomUUID(), work_id });
export const workCommand = (brand: string, work: string, body: unknown) => post(`${base(brand)}/work/${work}`, body);
export function workState(work: Work): string {
	if (work.status === "cancelled") return "cancelled";
	if (work.variants.some((v) => v.state === "unknown")) return "unknown";
	if (
		work.variants.length === work.config.channels.length &&
		work.variants.length > 0 &&
		work.variants.every((v) => v.state === "published")
	)
		return "published";
	if (work.variants.some((v) => v.state === "failed")) return "failed";
	if (work.variants.some((v) => v.state === "sending")) return "sending";
	if (work.variants.some((v) => v.state === "remote_draft")) return "remote_draft";
	return work.status;
}
